/**
 * People endpoints — overview, members, invitations, person-detail,
 * authorizations, freeze, role/rename mutations, account rename.
 */

import { ApiError, extractError, get, post, put } from '../_shared.ts';

// ---------------------------------------------------------------------
// Tags (Push 70b, D-198 wallet collapse)
//
// Backing endpoints:
//   - GET   /api/admin/account/tags        — distinct values across account
//   - PATCH /api/admin/people/[id]/tags    — full replace OR delta shape
// ---------------------------------------------------------------------

/** Distinct tag values across the caller's account. Powers TagChipEditor
 *  typeahead + BulkCapByTagModal + People-list filter. */
export function fetchAccountTags(): Promise<{ ok: true; values: string[] }> {
  return get('/api/admin/account/tags');
}

/** Full-replace tag write for a single user. accountUserId can be either
 *  the UUID or the usr-prefixed short_id. */
export async function updatePersonTags(
  accountUserId: string,
  tags: string[],
): Promise<{ ok: true; tags: string[] }> {
  const path = `/api/admin/people/${encodeURIComponent(accountUserId)}/tags`;
  const res = await fetch(path, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tags }),
  });
  const text = await res.text();
  if (!res.ok) {
    const info = extractError(text, path, 'PATCH', res.status);
    throw new ApiError(res.status, info.message, info.detail, info.incidentId, info.code);
  }
  return JSON.parse(text) as { ok: true; tags: string[] };
}


// ---------------------------------------------------------------------
// Admin-scoped reads
// ---------------------------------------------------------------------

export interface AdminOverview {
  account: {
    id: string;
    name: string;
    is_active: boolean;
    current_cycle_start: string | null;
    current_cycle_end: string | null;
    included_units_this_cycle: number | null;
    consumed_units_this_cycle: number | null;
  };
  counts: {
    active_users: number;
    pending_invitations: number;
    installed_connectors: number;
    installed_prompts: number;
    installed_models: number;
    dispatches_this_cycle: number;
    open_requests: number;
  };
  recent_activity: Array<{
    id: string;
    /** sys__event_log.short_id (evt-prefix) — deep-link identifier for
     *  /activity/:short_id. Added Push 4-1d smoke-fix (2026-07-02). */
    short_id: string;
    user_display_name: string | null;
    connector_name: string;
    status: string;
    duration_ms: number | null;
    created_at: string;
  }>;
}

export function fetchAdminOverview(): Promise<AdminOverview> {
  return get<AdminOverview>('/api/admin/overview');
}

export interface AdminPerson {
  account_user_id: string;
  /** Phase 2 Block 2A Cluster C — sys__account_user.short_id (usr-prefix) for
   *  /admin/people/:short_id deep-link from list rows. */
  short_id: string;
  user_id: string;
  email: string;
  display_name: string;
  role: 'owner' | 'admin' | 'user';
  /** Push 70b (D-198 wallet collapse) — free-form multi-value reporting
   *  tags. NULL or empty = untagged. Powers the Tags column + inline chip
   *  editor + BulkCapByTagModal filter. */
  tags: string[] | null;
  /** Per-user monthly spend cap (sys__account_user.monthly_hard_cap_credits).
   *  NULL = no cap (draws from team pot unlimited). Displayed on the
   *  People list so admins can spot capped-vs-uncapped users at a glance. */
  monthly_hard_cap_credits: string | null;
  is_active: boolean;
  email_verified: boolean;
  connector_count: number;
  dispatches_this_cycle: number;
  created_at: string;
}

export interface AdminInvitation {
  id: string;
  /** sys__user_invitation.short_id (inv-prefix) — powers /admin/invitations/:short_id drill-down. */
  short_id: string;
  email: string;
  invited_role: 'owner' | 'admin' | 'user';
  status: 'pending' | 'accepted' | 'expired' | 'revoked' | string;
  invited_by_display_name: string | null;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export function fetchAdminPeople(): Promise<{
  members: AdminPerson[];
  invitations: AdminInvitation[];
}> {
  return get('/api/admin/people');
}

export function updateMemberRole(
  accountUserId: string,
  role: 'owner' | 'admin' | 'user',
): Promise<{ ok: true }> {
  return post('/api/admin/people/role', { account_user_id: accountUserId, role });
}

export function deactivateMember(accountUserId: string): Promise<{ ok: true }> {
  return post('/api/admin/people/deactivate', { account_user_id: accountUserId });
}

export function reactivateMember(accountUserId: string): Promise<{ ok: true }> {
  return post('/api/admin/people/reactivate', { account_user_id: accountUserId });
}

export function inviteMember(
  email: string,
  role: 'admin' | 'user',
): Promise<{
  ok: true;
  invitation_id: string;
  invite_url: string;
  email_sent: boolean;
}> {
  // Push 70b (D-198 wallet collapse): wallet_policy param retired. Every
  // invited user now defaults to the single team-pot model; admins tune
  // per-user spend caps + tags after the invite is accepted.
  return post('/api/admin/invite', { email, role });
}

/**
 * Push #84 round 19 — hard-delete an invitation. Replaces revokeInvitation
 * per Ean's "deleted and they go away" UX framing. After this call the
 * invitation row is gone from sys__user_invitation; the corresponding
 * token immediately stops working.
 */
export function deleteInvitation(invitationId: string): Promise<{ ok: true }> {
  return post('/api/admin/invitations/delete', { invitation_id: invitationId });
}

/**
 * Push #84 round 19 — resend the Resend email for an existing PENDING
 * invitation. Rotates the token: the email carries a new link and the
 * old link stops working (PLAT-90). If the invite is
 * within 24h of expiry, also extends expires_at to now + 7d so the
 * recipient gets a fresh week.
 */
export function resendInvitation(invitationId: string): Promise<{ ok: true; email_sent: boolean }> {
  return post('/api/admin/invitations/resend', { invitation_id: invitationId });
}

export function renameAccountUser(
  accountUserId: string,
  displayName: string,
): Promise<{ ok: true; account_user_id: string; display_name: string }> {
  return post('/api/admin/people/rename', {
    account_user_id: accountUserId,
    display_name: displayName,
  });
}

export function updateAccountName(name: string): Promise<{ ok: true }> {
  return post('/api/admin/account/rename', { name });
}

/**
 * Push 4-1d-e (2026-07-01) — flip sys__account.timezone for the whole
 * workspace. Server-validates the IANA zone via Intl.DateTimeFormat probe;
 * invalid values return 400 with `reason: 'invalid_timezone'`. Requires
 * admin OR owner role (same gate as rename). Column-level GRANT lands in
 * Migration 0145.
 */
export function updateAccountTimezone(
  timezone: string,
): Promise<{ ok: true; timezone: string }> {
  return post('/api/admin/account/timezone', { timezone });
}

/**
 * Push 4-1d-e (2026-07-01) — GET the current workspace-default timezone.
 * Used by /admin/settings on mount to seed the timezone selector.
 */
export function fetchAccountTimezone(): Promise<{ timezone: string }> {
  return get('/api/admin/account/timezone');
}

// ---------------------------------------------------------------------
// Brand theming — Push 5-post-smoke-43 (2026-07-15). Migration 0154 added
// sys__account.brand_primary_color / brand_accent_color / brand_logo_r2_key
// / brand_logo_updated_at. NULL on any field = "use Olytic default".
// ---------------------------------------------------------------------

export interface WorkspaceThemeResponse {
  primary_color: string | null;
  accent_color: string | null;
  logo_r2_key: string | null;
  logo_updated_at: string | null;
}

export function fetchWorkspaceTheme(): Promise<WorkspaceThemeResponse> {
  return get<WorkspaceThemeResponse>('/api/admin/workspace/theme');
}

export function updateWorkspaceTheme(
  patch: { primary_color?: string | null; accent_color?: string | null },
): Promise<{ ok: true } & WorkspaceThemeResponse> {
  return put('/api/admin/workspace/theme', patch);
}

/**
 * Upload a workspace brand logo. Accepts PNG / JPEG / SVG / WebP up to
 * 500 KB. On success returns the new r2_key + logo_updated_at ISO ts
 * (drives the /workspace-logo/{account_id} ETag). SPA calls
 * session.refresh() after success so TopNav re-renders with the new
 * logo without a full page reload.
 */
export async function uploadWorkspaceLogo(
  file: File,
): Promise<{ ok: true; logo_r2_key: string; logo_updated_at: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/admin/workspace/logo', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    const err = parsed as { error?: string; message?: string } | null;
    throw new ApiError(
      res.status,
      err?.message ?? err?.error ?? `Upload failed (${res.status})`,
      null,
      null,
      err?.error ?? null,
    );
  }
  return parsed as { ok: true; logo_r2_key: string; logo_updated_at: string };
}

export async function deleteWorkspaceLogo(): Promise<{
  ok: true;
  logo_r2_key: null;
  logo_updated_at: null;
}> {
  const path = '/api/admin/workspace/logo';
  const res = await fetch(path, { method: 'DELETE', credentials: 'include' });
  const text = await res.text();
  if (!res.ok) {
    const errInfo = extractError(text, path, 'DELETE', res.status);
    throw new ApiError(
      res.status,
      errInfo.message,
      errInfo.detail,
      errInfo.incidentId,
      errInfo.code,
    );
  }
  return JSON.parse(text) as {
    ok: true;
    logo_r2_key: null;
    logo_updated_at: null;
  };
}

/**
 * D-148 universal-detail discipline — single-user record detail for the
 * /admin/people/:id Lightning page.
 */
export interface PersonDetailProfile {
  id: string;
  /** Phase 2 Block 2A Cluster C — sys__account_user.short_id (usr-prefix). */
  short_id: string;
  display_name: string;
  email: string;
  role: 'owner' | 'admin' | 'user';
  is_active: boolean;
  email_verified: boolean;
  experience_level: string | null;
  frozen_at: string | null;
  frozen_reason: string | null;
  usage_cap_this_period: number | null;
  /**
   * Push #4.5 Item 5 (Migration 0116) — admin can flip from PersonDetail;
   * user can flip from /profile. Both surfaces edit the same row through
   * separate endpoints; PersonDetail audits via 'user_confirm_writes_changed'.
   */
  confirm_writes_in_chat: boolean;
  created_at: string;
  last_active_at: string | null;
  permission_set_count: number;
  connector_assignment_count: number;
  prompt_assignment_count: number;
  model_assignment_count: number;
}
export interface PersonDetailPermissionSet {
  id: string;
  /** Phase 2 Block 2A close sweep — sys__permission_set.short_id (pst-prefix). */
  short_id: string;
  name: string;
  kind: 'owner_bootstrap' | 'standard_members' | 'admin_curated';
  granted_at: string;
}
export interface PersonDetailDirectGrant {
  id: string;
  instance_id: string;
  display_name: string;
  granted_at: string;
}
export interface PersonDetailActivity {
  id: string;
  event_type: string;
  kind: string | null;
  status: string | null;
  target_kind: string | null;
  target_id: string | null;
  created_at: string;
}
export interface PersonDetail {
  profile: PersonDetailProfile;
  permission_sets: PersonDetailPermissionSet[];
  direct_connectors: PersonDetailDirectGrant[];
  direct_prompts: PersonDetailDirectGrant[];
  direct_models: PersonDetailDirectGrant[];
  recent_activity: PersonDetailActivity[];
}
/**
 * Phase 2 Block 2A Cluster C (2026-06-30) — param renamed from `id` (UUID) to
 * `short_id` (sys__account_user.short_id, usr-prefix). Endpoint resolves the
 * UUID via short_id lookup; action endpoints continue to accept account_user_id
 * (UUID) in the body via legacy paths.
 */
export function fetchAdminPersonDetail(short_id: string): Promise<PersonDetail> {
  return get(`/api/admin/people/${encodeURIComponent(short_id)}`);
}

// Push #8 Session A — per-user endpoints
export interface PersonActivityRow {
  source: 'dispatch' | 'admin_action';
  id: string;
  kind?: string | null;
  status?: string | null;
  target_kind?: string | null;
  target_id?: string | null;
  event_type?: string;
  payload?: unknown;
  created_at: string;
}
/** Phase 2 Block 2A Cluster C — param is sys__account_user.short_id (usr-prefix). */
export function fetchPersonActivity(
  short_id: string,
  opts?: { limit?: number; offset?: number },
): Promise<{ rows: PersonActivityRow[]; limit: number; offset: number }> {
  const qs = new URLSearchParams();
  if (opts?.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts?.offset !== undefined) qs.set('offset', String(opts.offset));
  const tail = qs.toString();
  return get(`/api/admin/people/${encodeURIComponent(short_id)}/activity${tail === '' ? '' : `?${tail}`}`);
}

export interface PersonAuthorization {
  id: string;
  connector_instance_id: string;
  connector_display_name: string;
  connector_template_name: string;
  scope_granted: string[] | null;
  expires_at: string | null;
  last_refreshed_at: string | null;
  last_used_at: string | null;
  needs_reauth: boolean;
  refresh_failure_count: number;
  created_at: string;
}
/** Phase 2 Block 2A Cluster C — param is sys__account_user.short_id (usr-prefix). */
export function fetchPersonAuthorizations(short_id: string): Promise<{ authorizations: PersonAuthorization[] }> {
  return get(`/api/admin/people/${encodeURIComponent(short_id)}/authorizations`);
}
export function setPersonFreeze(id: string, frozen: boolean, reason?: string): Promise<{ ok: true }> {
  return post(`/api/admin/people/${encodeURIComponent(id)}/freeze`, {
    frozen,
    ...(reason !== undefined && reason !== '' ? { reason } : {}),
  });
}
export function revokePersonAuthorization(
  id: string,
  authorization_id: string,
): Promise<{ ok: true }> {
  return post(`/api/admin/people/${encodeURIComponent(id)}/revoke-authorization`, {
    authorization_id,
  });
}

/**
 * Push #4.5 Item 5 — admin override of a member's confirm_writes_in_chat
 * preference. Returns the persisted value so the caller can sync local
 * state on success. Audit row written server-side.
 */
export function setPersonConfirmWrites(
  id: string,
  confirm_writes_in_chat: boolean,
): Promise<{ ok: true; changed: boolean; confirm_writes_in_chat: boolean }> {
  return post(`/api/admin/people/${encodeURIComponent(id)}/confirm-writes`, {
    confirm_writes_in_chat,
  });
}

